// User timezone utilities for consistent date/time display
// Integrates with user context for timezone-aware formatting

interface User {
  timezone?: string;
  timezoneOffset?: number;
}

/**
 * Smart format date in user's timezone with fallback logic
 * Used by Kanban cards for due dates and other time-sensitive data
 */
export function smartFormatDateInUserTimezone(dateString: string, user?: User | null): string {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // If user has timezone preference, use it
    if (user?.timezone) {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: user.timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(date);
    }
    
    // Otherwise use local timezone
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString; // Return original string if parsing fails
  }
}

/**
 * Format date with time in user's timezone
 */
export function formatDateTimeInUserTimezone(dateString: string, user?: User | null): string {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    if (user?.timezone) {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: user.timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).format(date);
    }
    
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(date);
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return dateString;
  }
}

/**
 * Get user's timezone display name
 */
export function getUserTimezoneDisplay(user?: User | null): string {
  if (user?.timezone) {
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: user.timezone,
        timeZoneName: 'long'
      }).formatToParts(new Date())
        .find(part => part.type === 'timeZoneName')?.value || user.timezone;
    } catch (error) {
      return user.timezone;
    }
  }
  
  // Fallback to system timezone
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Convert UTC date to user's timezone
 */
export function convertToUserTimezone(utcDateString: string, user?: User | null): Date {
  const utcDate = new Date(utcDateString);
  
  if (user?.timezone) {
    // Create a date in the user's timezone
    return new Date(utcDate.toLocaleString('en-US', { timeZone: user.timezone }));
  }
  
  return utcDate;
}

/**
 * Get current date/time in user's timezone as ISO string
 */
export function getCurrentDateInUserTimezone(user?: User | null): string {
  const now = new Date();
  
  if (user?.timezone) {
    // Get current time in user's timezone
    const userTime = new Date(now.toLocaleString('en-US', { timeZone: user.timezone }));
    return userTime.toISOString();
  }
  
  return now.toISOString();
}

/**
 * Check if a date is today in user's timezone
 */
export function isToday(dateString: string, user?: User | null): boolean {
  if (!dateString) return false;
  
  try {
    const date = new Date(dateString);
    const today = new Date();
    
    if (user?.timezone) {
      const userDate = new Date(date.toLocaleString('en-US', { timeZone: user.timezone }));
      const userToday = new Date(today.toLocaleString('en-US', { timeZone: user.timezone }));
      
      return userDate.toDateString() === userToday.toDateString();
    }
    
    return date.toDateString() === today.toDateString();
  } catch (error) {
    return false;
  }
}

/**
 * Format date as ISO string in user's timezone (for HTML date inputs)
 */
export function formatDateAsUserTimezoneISO(dateString: string, user?: User | null): string {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    if (user?.timezone) {
      // Get the date in user's timezone
      const userDate = new Date(date.toLocaleString('en-US', { timeZone: user.timezone }));
      // Return in YYYY-MM-DD format for HTML date input
      return userDate.toISOString().split('T')[0];
    }
    
    // Use local timezone
    return date.toISOString().split('T')[0];
  } catch (error) {
    console.error('Error formatting date as ISO:', error);
    return dateString;
  }
}

/**
 * Get relative time string (e.g., "2 hours ago", "tomorrow")
 */
export function getRelativeTime(dateString: string, user?: User | null): string {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    const now = new Date();
    
    if (user?.timezone) {
      const userDate = new Date(date.toLocaleString('en-US', { timeZone: user.timezone }));
      const userNow = new Date(now.toLocaleString('en-US', { timeZone: user.timezone }));
      
      const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
      const diffInSeconds = Math.floor((userDate.getTime() - userNow.getTime()) / 1000);
      
      if (Math.abs(diffInSeconds) < 60) return 'now';
      if (Math.abs(diffInSeconds) < 3600) return rtf.format(Math.round(diffInSeconds / 60), 'minute');
      if (Math.abs(diffInSeconds) < 86400) return rtf.format(Math.round(diffInSeconds / 3600), 'hour');
      return rtf.format(Math.round(diffInSeconds / 86400), 'day');
    }
    
    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    const diffInSeconds = Math.floor((date.getTime() - now.getTime()) / 1000);
    
    if (Math.abs(diffInSeconds) < 60) return 'now';
    if (Math.abs(diffInSeconds) < 3600) return rtf.format(Math.round(diffInSeconds / 60), 'minute');
    if (Math.abs(diffInSeconds) < 86400) return rtf.format(Math.round(diffInSeconds / 3600), 'hour');
    return rtf.format(Math.round(diffInSeconds / 86400), 'day');
  } catch (error) {
    console.error('Error calculating relative time:', error);
    return smartFormatDateInUserTimezone(dateString, user);
  }
}